/**
 * Global URL parameter manager.
 * Captures the full query string on first load before the app rewrites the URL (e.g. to remove jwt).
 * Call initUrlParams() once in main.tsx before rendering.
 */

let initialSearch: string | null = null;
let extractedJwt: string | null = null;

/**
 * Initialize URL params and strip jwt from the address bar.
 * Call once before createRoot. Idempotent.
 * @returns The extracted jwt if present, otherwise null.
 */
export function initUrlParams(): string | null {
  if (initialSearch !== null) {
    return extractedJwt;
  }

  initialSearch = typeof window !== 'undefined' ? window.location.search : '';

  const params = new URLSearchParams(initialSearch);
  // Accept both `jwt` and the common typo `jws`.
  const jwt = params.get('jwt') ?? params.get('jws');

  if (jwt) {
    extractedJwt = jwt;
    params.delete('jwt');
    params.delete('jws');
    const remaining = params.toString();
    const pathname = window.location.pathname;
    const newUrl = remaining ? `${pathname}?${remaining}` : pathname;
    window.history.replaceState(null, '', newUrl);
  }

  return extractedJwt;
}

/**
 * Read-only snapshot of the original query string from first load.
 * Use this instead of window.location.search after init so params are stable after jwt strip.
 */
export function getInitialUrlParams(): URLSearchParams {
  const search = initialSearch ?? (typeof window !== 'undefined' ? window.location.search : '');
  return new URLSearchParams(search);
}

/**
 * JWT extracted from the URL during init. Only set after initUrlParams() has run.
 */
export function getInitialJwt(): string | null {
  return extractedJwt;
}

/**
 * Embed mode: `?embed=1` marks this tab as hosted inside another shell (the
 * Beatly BO renders Omni in a frame under its Agents section). The host
 * provides navigation and chrome, so Omni hides its own top bar and sidebar.
 * Persisted per-tab: in-app navigation after the first load keeps the mode.
 */
export function isEmbedMode(): boolean {
  try {
    if (getInitialUrlParams().get('embed') === '1') {
      sessionStorage.setItem('omni_embed', '1');
      return true;
    }
    return sessionStorage.getItem('omni_embed') === '1';
  } catch {
    return false;
  }
}
